import React, { useState } from "react";
import { useI18n } from "bananas-commerce-admin";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";

import dayjs from "dayjs";

import { DaysType, PublishingRuleCreateSchema } from "../../types";
import { daysType, toTitleCase } from "../../utils";
import AddButton from "../AddButton";
import DateField from "../DateField";
import RemoveButton from "../RemoveButton";
import TimeField from "../TimeField";

export interface StoptimesSectionProps {
  schedule: PublishingRuleCreateSchema;
  setSchedule: React.Dispatch<React.SetStateAction<PublishingRuleCreateSchema>>;
}

const StoptimesSection: React.FC<StoptimesSectionProps> = ({
  schedule,
  setSchedule,
}) => {
  const [isActive, setIsActive] = useState(
    schedule.times_exceptions.length > 0,
  );
  const { t } = useI18n();
  const handleAddExcludedDate = () => {
    setSchedule((prev: PublishingRuleCreateSchema) => ({
      ...prev,
      times_exceptions: [
        ...prev.times_exceptions,
        {
          for_date: prev.from_date,
          deadline_date: prev.to_date,
          hour: prev.hour,
          minute: prev.minute,
        },
      ],
    }));
  };

  const handleRemoveExcludedDate = (index: number) => {
    setSchedule((prev: PublishingRuleCreateSchema) => ({
      ...prev,
      times_exceptions: prev.times_exceptions.filter((_, i) => i !== index),
    }));
  };

  return (
    <>
      <Box>
        <Typography gutterBottom variant="subtitle1">
          {t("Stopptid")}
        </Typography>

        <Stack direction="row" mt={2} spacing={2}>
          {/* TODO Use text field instead of somewhat wonky mui time field? */}
          <TimeField
            value={dayjs().hour(schedule.hour).minute(schedule.minute)}
            onChange={(date) => {
              if (date) {
                const hours = date.hour();
                const minutes = date.minute();
                setSchedule((prev: PublishingRuleCreateSchema) => ({
                  ...prev,
                  hour: hours,
                  minute: minutes,
                }));
              }
            }}
          />
          <FormControl sx={{ flex: 1 }}>
            <InputLabel size="small">{t("Antal")}</InputLabel>
            <Select
              label={t("Antal")}
              size="small"
              sx={{ bgcolor: "backgrond.paper" }}
              value={schedule.publication_lead_time}
              onChange={(e) => {
                setSchedule((prev: PublishingRuleCreateSchema) => ({
                  ...prev,
                  publication_lead_time: Number(e.target.value),
                }));
              }}
            >
              {[...Array(7)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel size="small">{t("Dagtyp")}</InputLabel>
            <Select
              label={t("Dagtyp")}
              size="small"
              sx={{ bgcolor: "backgrond.paper" }}
              value={schedule.days_type}
              onChange={(e) => {
                setSchedule((prev: PublishingRuleCreateSchema) => ({
                  ...prev,
                  days_type: e.target.value as DaysType,
                }));
              }}
            >
              <MenuItem value="business_days">
                {toTitleCase(daysType["business_days"])}
              </MenuItem>
              <MenuItem value="any_days">
                {toTitleCase(daysType["any_days"])}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Typography gutterBottom color="text.secondary" variant="caption">
          {t("Ange klockslag och antal dagar innan utgivningsdag")}
        </Typography>
      </Box>
      <Box>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="subtitle1">
            {t("Anpassade stopptider")}
          </Typography>
          <Switch
            checked={isActive}
            onChange={(e) => {
              setIsActive(e.target.checked);
              if (!e.target.checked) {
                setSchedule((prev: PublishingRuleCreateSchema) => ({
                  ...prev,
                  times_exceptions: [],
                }));
              } else {
                handleAddExcludedDate();
              }
            }}
          />
        </Stack>
        <Typography gutterBottom color="text.secondary" variant="caption">
          {t("Ange dagar med avvikande stopptider")}
        </Typography>
        {isActive && (
          <Box display="grid" gap={1} gridTemplateColumns="1fr 1fr 1fr auto">
            <Box
              display="grid"
              gap={1}
              gridTemplateColumns="subgrid"
              sx={{ gridColumn: "1 / -1" }}
            >
              <Typography color="text.secondary">{t("Gäller")}</Typography>
              <Typography color="text.secondary">{t("Stoppdag")}</Typography>
              <Typography color="text.secondary">{t("Klockslag")}</Typography>
            </Box>
            {schedule.times_exceptions.map((exception, index) => (
              <Box
                key={index}
                alignItems="center"
                display="grid"
                gap={1}
                gridTemplateColumns="subgrid"
                sx={{ gridColumn: "1 / -1" }}
              >
                <DateField
                  datePickerProps={{
                    sx: { gridColumn: "span 1" },
                  }}
                  value={dayjs(exception.for_date)}
                  onChange={(date) => {
                    if (date) {
                      setSchedule((prev: PublishingRuleCreateSchema) => ({
                        ...prev,
                        times_exceptions: prev.times_exceptions.map((ex, i) =>
                          i === index
                            ? { ...ex, for_date: date.format("YYYY-MM-DD") }
                            : ex,
                        ),
                      }));
                    }
                  }}
                />
                <DateField
                  datePickerProps={{
                    sx: { gridColumn: "span 1" },
                  }}
                  value={dayjs(exception.deadline_date)}
                  onChange={(date) => {
                    if (date) {
                      setSchedule((prev: PublishingRuleCreateSchema) => ({
                        ...prev,
                        times_exceptions: prev.times_exceptions.map((ex, i) =>
                          i === index
                            ? {
                                ...ex,
                                deadline_date: date.format("YYYY-MM-DD"),
                              }
                            : ex,
                        ),
                      }));
                    }
                  }}
                />
                {/* TODO Use text field instead of somewhat wonky mui time field? */}
                <TimeField
                  timePickerProps={{
                    sx: { gridColumn: "span 1" },
                  }}
                  value={dayjs().hour(exception.hour).minute(exception.minute)}
                  onChange={(date) =>
                    setSchedule((prev: PublishingRuleCreateSchema) => ({
                      ...prev,
                      times_exceptions: prev.times_exceptions.map((ex, i) =>
                        i === index
                          ? {
                              ...ex,
                              hour: date?.hour() ?? 0,
                              minute: date?.minute() ?? 0,
                            }
                          : ex,
                      ),
                    }))
                  }
                />
                <Box sx={{ gridColumn: "span 1" }}>
                  <RemoveButton
                    onClick={() => handleRemoveExcludedDate(index)}
                  />
                </Box>
              </Box>
            ))}
            <Box>
              <AddButton onClick={handleAddExcludedDate} />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default StoptimesSection;
